import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { t } from "i18next";
import { toast } from "react-toastify";
import { queryClient } from "../../api";
import { memoirsApiInstance } from "../../api/api";
import { User } from "../../types/models";
import { RootState } from "../store";

export type AuthState = {
  userToken: string | null;
  user: User | null;
  impersonating: {
    adminToken: string;
    adminUser: User;
  } | null;
};

type SignOutResponse = {
  success: boolean;
  single_log_out_url?: string;
};

const initialState: AuthState = {
  userToken: null,
  user: null,
  impersonating: null,
};

export const signOut = createAsyncThunk(
  "auth/signOut",
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;

    try {
      if (state.auth.userToken) {
        await memoirsApiInstance
          .delete<SignOutResponse>("/sign_out", {
            headers: { Authorization: state.auth.userToken },
          })
          .then(() => {
            toast.success(t("logout.success"));
          });
      }
    } catch (error) {
      toast.error(t("logout.error"));
      return rejectWithValue(error);
    } finally {
      queryClient.clear();
    }
  }
);

const slice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<{ user: User }>) {
      state.user = action.payload.user;
    },
    setUserToken(state, action: PayloadAction<AuthState["userToken"]>) {
      state.userToken = action.payload;
    },
    reset: (state) => {
      state.userToken = null;
      state.user = null;
      state.impersonating = null;
    },
    startImpersonation(
      state,
      action: PayloadAction<{ user: User; token: string; adminToken: string; adminUser: User }>
    ) {
      state.impersonating = {
        adminToken: action.payload.adminToken,
        adminUser:  action.payload.adminUser,
      };
      state.user = action.payload.user;
      state.userToken = action.payload.token;
    },
    stopImpersonation(state, action: PayloadAction<{ user: User; token: string }>) {
      state.impersonating = null;
      state.user = action.payload.user;
      state.userToken = action.payload.token;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(signOut.fulfilled, () => initialState); // Réinitialisation après succès
    builder.addCase(signOut.rejected, () => initialState); // Réinitialisation après échec
  },
});

export const { setUserToken, setUser, reset, startImpersonation, stopImpersonation } = slice.actions;

export const selectUserToken = (state: RootState) => state.auth.userToken;
export const selectUser = (state: RootState) => state.auth.user;
export const selectImpersonating = (state: RootState) => state.auth.impersonating;

export default slice.reducer;
